import SidePanel from '../SidePanel.jsx';
import AdminSchedulingQuestion from './AdminSchedulingQuestion.jsx';
import { useEffect, useState } from 'react';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import * as Toast from '../../lib/Toast.js';
import { formatDateLong, formatDateTime } from '../../lib/DateTime.js';
import InfoMessageBlock from '../InfoMessageBlock.jsx';
import { useEventTypesQuery } from '../../lib/SharedQueries.js';
import OptionToggle from '~/components/OptionToggle';

const ReschedulingPanel = ({
    isOpen,
    onClose,
    event,
    start,
    resourceId,
    onRescheduled,
}) => {
    const [schedulingData, setSchedulingData] = useState({});
    const [hasInitialStart, setHasInitialStart] = useState(false);
    const [isUnavailable, setIsUnavailable] = useState(false);
    const [dontTriggerFlows, setDontTriggerFlows] = useState(false);
    const { data: allEventTypesData } = useEventTypesQuery();

    const { save, isSaving } = useBusinessPostApi(
        `/schedule/reschedule/${event?.id}`,
    );

    useEffect(() => {
        if (isOpen) {
            setDontTriggerFlows(false);
            setTimeout(() => {
                let newStart = start || event.client_start;

                // If we're rescheduling an all day event and it's set to midnight then preserve
                //  the same start hour as the original event
                if (
                    start &&
                    event?.event_type?.is_all_day &&
                    new Date(newStart).getHours() === 0
                ) {
                    newStart = new Date(newStart);
                    newStart.setHours(new Date(event.client_start).getHours());
                }

                setSchedulingData([
                    {
                        bookableResource:
                            parseInt(resourceId) ||
                            event.all_bookable_resource_ids,
                        eventType: event.event_type?.id || event.event_type_id,
                        startTime: newStart,
                    },
                ]);
            }, 1);

            if (start) {
                setHasInitialStart(true);
            }
        }
    }, [isOpen]);

    function doSave() {
        if (isUnavailable) {
            if (
                !confirm(
                    'This time is outside business hours or conflicts. Are you sure you want to reschedule it?',
                )
            ) {
                return;
            }
        }

        // Reschedule and call onRescheduled
        save({
            scheduling: schedulingData,
            dontTriggerFlows: dontTriggerFlows,
        }).then(result => {
            Toast.success(
                `Successfully rescheduled to ${formatDateTime(result.client_start)}`,
            );
            onClose();
            onRescheduled(result);
        });
    }

    const questionSettings = {
        enableRecurring: false,
        hideQuantity: true,
    };
    if (allEventTypesData) {
        questionSettings['eventTypes'] = allEventTypesData
            .filter(t => {
                // If event is a series, only give series options
                if (event?.event_type?.is_series) {
                    return t.is_series;
                }

                // Otherwise exclude series, and sometimes classes
                return (!start || !t.is_class) && !t.is_series;
            })
            .map(t => t.id);
    }

    return (
        <SidePanel
            isOpen={isOpen}
            onClose={onClose}
            title={`Reschedule ${event?.client?.name}`}
            showSave={true}
            saveLabel={isSaving ? 'Rescheduling...' : 'Reschedule'}
            onCancel={onClose}
            onSave={doSave}
        >
            <InfoMessageBlock showIcon={true} className="mb-4">
                <div className="font-medium">Original Event:</div>
                <div>{event?.event_type?.name}</div>
                <div>
                    {!!event?.event_type?.is_all_day
                        ? formatDateLong(event?.client_start)
                        : formatDateTime(event?.client_start)}
                </div>
            </InfoMessageBlock>

            <div className=" pb-24 sm:pb-8">
                <AdminSchedulingQuestion
                    key={event?.id}
                    value={schedulingData}
                    setValue={value => {
                        //setSchedulingData((old) => ({...old, ...value}))
                        setSchedulingData(old => {
                            if (typeof value === 'function') {
                                value = value(old);
                            }

                            return value;
                        });
                    }}
                    hasInitialStart={hasInitialStart}
                    isUnavailable={isUnavailable}
                    setIsUnavailable={setIsUnavailable}
                    settings={{
                        ignoreEvents: [event?.id],
                        questionSettings: questionSettings,
                    }}
                />

                <OptionToggle
                    value={!dontTriggerFlows}
                    onChange={value => setDontTriggerFlows(!value)}
                    className="text-gray-500 text-sm flex-row-reverse gap-x-4"
                    title="Send automations when rescheduled"
                />
            </div>
        </SidePanel>
    );
};

export default ReschedulingPanel;
